import { useEffect, useMemo, useRef } from 'react';
import { useStore } from '@tanstack/react-form';
import { useLazyGetDefaultEmployeeReviewPeriodQuery } from '../employee-review/api/employeeReviewApi';
import { extractErrorMessage } from '../../utils/errorUtils';
import type { DetailFormJsMethodProps } from './registry';
import {
  parseEmployeeReviewPeriodJsMethodOptions,
  resolveEmployeeReviewPeriodFieldName,
} from './employeeReviewPeriodOptions';
import { useJsMethodLoading } from './jsMethodLoadingContext';

const isEmpty = (value: unknown): boolean => value == null || value === '';
const loadedForms = new WeakSet<object>();

export const EmployeeReviewPeriodJsMethodBinding = ({
  field,
  fields,
  form,
  onNotify,
}: DetailFormJsMethodProps) => {
  const { setLoading } = useJsMethodLoading();
  const [getDefaultPeriod] = useLazyGetDefaultEmployeeReviewPeriodQuery();
  const hasLoaded = useRef(false);
  const loadingKey = `${field.key ?? field.dataField ?? field.field}:${field.nameJsMethod}`;
  const options = useMemo(
    () => parseEmployeeReviewPeriodJsMethodOptions(field.jsMethodOptions),
    [field.jsMethodOptions]
  );
  const periodFromFieldName = field.dataField ?? field.field;
  const periodToFieldName = useMemo(
    () =>
      options ? resolveEmployeeReviewPeriodFieldName(fields, options.periodToIdElement) : null,
    [fields, options]
  );
  const idFieldName = useMemo(
    () =>
      fields.find(
        (candidate) => (candidate.dataField ?? candidate.field ?? '').toLowerCase() === 'id'
      )?.dataField ?? 'Id',
    [fields]
  );
  const formValues = useStore(form.store, (state) => {
    return (state as { values?: Record<string, unknown> }).values ?? {};
  });
  const formValuesRef = useRef(formValues);

  useEffect(() => {
    formValuesRef.current = formValues;
  }, [formValues]);

  useEffect(() => {
    if (hasLoaded.current) return;
    hasLoaded.current = true;

    if (Number(formValuesRef.current[idFieldName]) > 0 || !options || !periodToFieldName) return;
    if (loadedForms.has(form)) return;
    loadedForms.add(form);

    let isActive = true;
    setLoading(loadingKey, true);
    void getDefaultPeriod()
      .unwrap()
      .then((period) => {
        if (!isActive) return;
        const currentValues = formValuesRef.current;
        if (isEmpty(currentValues[periodFromFieldName]) && period.periodFrom) {
          form.setFieldValue(periodFromFieldName, period.periodFrom, { dontUpdateMeta: true });
        }
        if (isEmpty(currentValues[periodToFieldName]) && period.periodTo) {
          form.setFieldValue(periodToFieldName, period.periodTo, { dontUpdateMeta: true });
        }
      })
      .catch((error) => {
        if (isActive) {
          onNotify({
            severity: 'error',
            message: extractErrorMessage(error, 'Failed to resolve the employee review period.'),
          });
        }
      })
      .finally(() => {
        if (isActive) setLoading(loadingKey, false);
      });

    return () => {
      isActive = false;
      setLoading(loadingKey, false);
    };
  }, [
    form,
    idFieldName,
    getDefaultPeriod,
    loadingKey,
    onNotify,
    options,
    periodFromFieldName,
    periodToFieldName,
    setLoading,
  ]);

  return null;
};
